import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowClockwise, Check, CircleNotch, Funnel, PencilSimple, Plus, Tag, Trash, TreeStructure, WarningCircle, X,
} from '@phosphor-icons/react';
import {
  BestaetigenDialog, FeldRaster, ImmoButton, InnenKarte, KartenTitel, ListenPanel, PanelZeile, SchalterZeile, SearchSelect, SegmentedControl, TextFeld,
  ToastPill, useToastPill,
} from '../../../../components/immobilien/ui';
import type { KategorieRoh } from '../../../../lib/dienstleister/liste';
import {
  besterVorschlag, kategorieLoeschbar, PRIORITAETEN, prioTon, regelFehler, regelPayload, type Prioritaet, type RegelEntwurf, type VorschlagKandidat,
} from '../../../../lib/dienstleister/vorschlag';
import {
  aendereKategorie, ladeRegelDaten, legeKategorieAn, loescheKategorieEndgueltig, loescheRegel, setzeRegelAktiv, speichereRegel, type Auswahl, type RegelZeile,
} from '../../../../lib/dienstleister/aktionen';

const fehlerText = (e: unknown) => (e instanceof Error ? e.message : 'Unbekannter Fehler');
const LEER: RegelEntwurf = {
  name: '', stichwoerter: '', prioritaet: 'mittel', ist_notfall: false, nur_mit_anhang: false, ist_rechtlich_sensibel: false,
  kategorie_id: '', gewerk_id: '', bearbeiter_team_id: '', aktiv: true,
};
const entwurfAus = (r: RegelZeile): RegelEntwurf => ({
  name: r.name ?? '', stichwoerter: r.stichwoerter.join(', '), prioritaet: (PRIORITAETEN.some(p => p.key === r.prioritaet) ? r.prioritaet : 'mittel') as Prioritaet,
  ist_notfall: r.ist_notfall, nur_mit_anhang: r.nur_mit_anhang, ist_rechtlich_sensibel: r.ist_rechtlich_sensibel,
  kategorie_id: r.kategorie_id ?? '', gewerk_id: r.gewerk_id ?? '', bearbeiter_team_id: r.bearbeiter_team_id ?? '', aktiv: r.aktiv,
});

type RegelDaten = { regeln: RegelZeile[]; ticketKategorien: Auswahl[]; teams: Auswahl[] };

/** Kategorien & Ticket-Regeln (Handoff Dienstleister §7, Screens 08/09) – ersetzt KategorienAdmin und RegelnAdmin. */
export function KategorienRegelnPanel({ kategorien, dlJeKategorie, kandidaten, onSchliessen, onGeaendert }: {
  kategorien: readonly KategorieRoh[];
  dlJeKategorie: ReadonlyMap<string, number>;
  kandidaten: readonly VorschlagKandidat[];
  onSchliessen: () => void;
  /** Liste neu laden (Kategorien gehören zu den Listendaten) */
  onGeaendert: () => void;
}) {
  const [toast, zeige] = useToastPill();
  const [laeuft, setLaeuft] = useState(false);
  const [neueKat, setNeueKat] = useState('');
  const [loeschen, setLoeschen] = useState<{ art: 'kategorie' | 'regel'; id: string; name: string } | null>(null);
  const [daten, setDaten] = useState<{ status: 'laedt' } | { status: 'fehler'; meldung: string } | { status: 'ok'; d: RegelDaten }>({ status: 'laedt' });
  const [form, setForm] = useState<{ id: string | null; e: RegelEntwurf } | null>(null);

  const lade = useCallback(() => {
    setDaten({ status: 'laedt' });
    ladeRegelDaten().then(d => setDaten({ status: 'ok', d })).catch((e: unknown) => setDaten({ status: 'fehler', meldung: fehlerText(e) }));
  }, []);
  useEffect(() => { lade(); }, [lade]);

  const ausfuehren = async (fn: () => Promise<void>, text: string, danach: 'liste' | 'regeln') => {
    setLaeuft(true);
    try { await fn(); zeige(text); if (danach === 'liste') onGeaendert(); else lade(); } catch (e) { zeige(fehlerText(e), true); } finally { setLaeuft(false); }
  };

  const regeln = daten.status === 'ok' ? daten.d.regeln : [];
  const regelnJeGewerk = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of regeln) if (r.gewerk_id) m.set(r.gewerk_id, (m.get(r.gewerk_id) ?? 0) + 1);
    return m;
  }, [regeln]);
  const katName = new Map(kategorien.map(k => [k.id, k.name]));
  const ticketKat = new Map((daten.status === 'ok' ? daten.d.ticketKategorien : []).map(k => [k.id, k.name]));
  const sortiert = [...kategorien].sort((a, b) => Number(b.aktiv) - Number(a.aktiv) || (a.sortierung ?? 0) - (b.sortierung ?? 0) || a.name.localeCompare(b.name, 'de'));

  const kategorieAnlegen = () => {
    const name = neueKat.trim();
    if (!name) return;
    if (kategorien.some(k => k.name.trim().toLowerCase() === name.toLowerCase())) { zeige(`Kategorie „${name}“ gibt es schon`, true); return; }
    void ausfuehren(async () => { await legeKategorieAn(name); setNeueKat(''); }, `Kategorie „${name}“ angelegt`, 'liste');
  };

  return (
    <ListenPanel icon={TreeStructure} titel="Kategorien & Ticket-Regeln" sub="Steuern, welcher Dienstleister bei einem Ticket vorgeschlagen wird" onSchliessen={onSchliessen}>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      {loeschen && (
        <BestaetigenDialog titel={`${loeschen.art === 'kategorie' ? 'Kategorie' : 'Regel'} „${loeschen.name}“ löschen?`}
          text={loeschen.art === 'kategorie' ? 'Die Kategorie wird endgültig entfernt. Es hängen keine Dienstleister und keine Regeln daran.' : 'Die Regel wird endgültig entfernt. Stattdessen lässt sie sich auch nur deaktivieren.'}
          bestaetigenLabel="Endgültig löschen" laeuft={laeuft} onAbbrechen={() => setLoeschen(null)}
          onBestaetigen={() => {
            const l = loeschen;
            void ausfuehren(() => (l.art === 'kategorie' ? loescheKategorieEndgueltig(l.id) : loescheRegel(l.id)), `„${l.name}“ gelöscht`, l.art === 'kategorie' ? 'liste' : 'regeln')
              .then(() => { setLoeschen(null); if (l.art === 'regel') setForm(null); });
          }} />
      )}

      <InnenKarte padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}><KartenTitel>{`${kategorien.length} Kategorien`}</KartenTitel></div>
        {sortiert.map(k => {
          const n = dlJeKategorie.get(k.id) ?? 0;
          const loeschbar = !k.aktiv && kategorieLoeschbar(n, regelnJeGewerk.get(k.id) ?? 0);
          return (
            <div key={k.id} className="flex items-center min-w-0" style={{ gap: 4 }}>
              <div className="flex-1 min-w-0">
                <PanelZeile icon={Tag} ton={!k.aktiv ? 'neutral' : k.ist_handwerk ? 'acc' : 'info'} aktiv={k.aktiv} titel={k.name}
                  sub={`${n} Dienstleister · ${k.ist_handwerk ? 'Handwerk (§ 48b relevant)' : 'Dienstleistung'}${k.aktiv ? '' : ' · inaktiv'} · antippen: Handwerk umschalten`}
                  chip={n === 0 && k.aktiv ? { text: 'ohne Dienstleister', ton: 'warn' } : undefined}
                  onClick={laeuft ? undefined : () => void ausfuehren(() => aendereKategorie(k.id, { ist_handwerk: !k.ist_handwerk }), `${k.name}: ${k.ist_handwerk ? 'Dienstleistung' : 'Handwerk'}`, 'liste')}
                  ariaLabel={`${k.name}: Handwerk umschalten`} />
              </div>
              <ImmoButton groesse={32} icon={k.aktiv ? Trash : ArrowClockwise} disabled={laeuft}
                aria-label={k.aktiv ? `${k.name} deaktivieren` : `${k.name} aktivieren`} title={k.aktiv ? 'Deaktivieren' : 'Wieder aktivieren'}
                onClick={() => void ausfuehren(() => aendereKategorie(k.id, { aktiv: !k.aktiv }), `${k.name} ${k.aktiv ? 'deaktiviert' : 'aktiviert'}`, 'liste')} />
              {loeschbar && (
                <ImmoButton groesse={32} art="bad" icon={X} disabled={laeuft} aria-label={`${k.name} endgültig löschen`} title="Endgültig löschen"
                  onClick={() => setLoeschen({ art: 'kategorie', id: k.id, name: k.name })} />
              )}
            </div>
          );
        })}
      </InnenKarte>
      <InnenKarte>
        <KartenTitel>Neue Kategorie</KartenTitel>
        <div className="flex flex-wrap items-end" style={{ gap: 8 }}>
          <div className="flex-1" style={{ minWidth: 200 }}>
            <TextFeld label="Name" icon={Tag} placeholder="Neue Kategorie…" wert={neueKat} onChange={setNeueKat}
              onKeyDown={e => { if (e.key === 'Enter') kategorieAnlegen(); }} />
          </div>
          <ImmoButton groesse={44} icon={Plus} disabled={laeuft || !neueKat.trim()} onClick={kategorieAnlegen}>Hinzufügen</ImmoButton>
        </div>
      </InnenKarte>

      <InnenKarte padding="14px 12px 10px" gap={4}>
        <div style={{ padding: '0 6px 6px' }}>
          <KartenTitel rechts={!form ? <ImmoButton groesse={36} icon={Plus} onClick={() => setForm({ id: null, e: LEER })}>Neue Regel</ImmoButton> : undefined}>Ticket-Regeln</KartenTitel>
        </div>
        {daten.status === 'laedt' && <span className="flex items-center" style={{ gap: 8, padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}><CircleNotch size={14} className="animate-spin" />Regeln werden geladen …</span>}
        {daten.status === 'fehler' && (
          <span role="alert" className="flex items-center flex-wrap" style={{ gap: 8, padding: '4px 8px 10px', fontSize: 13, color: 'var(--bad)' }}>
            <WarningCircle size={14} weight="fill" />{daten.meldung}<ImmoButton groesse={32} icon={ArrowClockwise} onClick={lade}>Erneut versuchen</ImmoButton>
          </span>
        )}
        {daten.status === 'ok' && regeln.length === 0 && <span style={{ padding: '4px 8px 10px', fontSize: 13, color: 'var(--ink3)' }}>Noch keine Regeln angelegt</span>}
        {regeln.map(r => {
          const vorschlag = besterVorschlag(r, kandidaten);
          const ziel = [r.gewerk_id ? katName.get(r.gewerk_id) : '', r.kategorie_id ? ticketKat.get(r.kategorie_id) : ''].filter(Boolean).join(' / ') || 'ohne Kategorie';
          const sub = [
            `${r.stichwoerter.map(s => `„${s}“`).join(', ')} → ${ziel}`,
            r.gewerk_id ? (vorschlag ? `Vorschlag: ${vorschlag.name}` : 'kein Dienstleister') : 'kein Gewerk hinterlegt',
            r.ist_notfall ? 'Notfall' : '', r.nur_mit_anhang ? 'nur mit Anhang' : '', r.ist_rechtlich_sensibel ? 'rechtlich sensibel' : '',
          ].filter(Boolean).join(' · ');
          const name = r.name || 'Ohne Bezeichnung';
          return (
            <div key={r.id} className="flex items-center min-w-0" style={{ gap: 4 }}>
              <div className="flex-1 min-w-0">
                <PanelZeile icon={Funnel} ton={r.aktiv ? 'acc' : 'neutral'} aktiv={r.aktiv} titel={r.aktiv ? name : `${name} (inaktiv)`} sub={sub}
                  chip={{ text: r.prioritaet, ton: prioTon(r.prioritaet) }}
                  onClick={laeuft ? undefined : () => void ausfuehren(() => setzeRegelAktiv(r.id, !r.aktiv), `${name} ${r.aktiv ? 'deaktiviert' : 'aktiviert'}`, 'regeln')}
                  ariaLabel={`${name} ${r.aktiv ? 'deaktivieren' : 'aktivieren'}`} />
              </div>
              <ImmoButton groesse={32} icon={PencilSimple} aria-label={`${name} bearbeiten`} title="Bearbeiten" onClick={() => setForm({ id: r.id, e: entwurfAus(r) })} />
            </div>
          );
        })}
      </InnenKarte>

      {form && daten.status === 'ok' && (
        <RegelFormular key={form.id ?? 'neu'} start={form.e} neu={form.id === null} kategorien={kategorien} ticketKategorien={daten.d.ticketKategorien} teams={daten.d.teams}
          kandidaten={kandidaten} laeuft={laeuft} onAbbrechen={() => setForm(null)}
          onLoeschen={form.id ? () => setLoeschen({ art: 'regel', id: form.id as string, name: form.e.name || 'Regel' }) : undefined}
          onSpeichern={e => void ausfuehren(async () => { await speichereRegel(form.id, regelPayload(e)); setForm(null); }, 'Regel gespeichert', 'regeln')} />
      )}
    </ListenPanel>
  );
}

function RegelFormular({ start, neu, kategorien, ticketKategorien, teams, kandidaten, laeuft, onAbbrechen, onSpeichern, onLoeschen }: {
  start: RegelEntwurf; neu: boolean;
  kategorien: readonly KategorieRoh[]; ticketKategorien: readonly Auswahl[]; teams: readonly Auswahl[];
  kandidaten: readonly VorschlagKandidat[];
  laeuft: boolean;
  onAbbrechen: () => void; onSpeichern: (e: RegelEntwurf) => void; onLoeschen?: () => void;
}) {
  const [e, setE] = useState<RegelEntwurf>(start);
  const setze = <K extends keyof RegelEntwurf>(k: K) => (v: RegelEntwurf[K]) => setE(x => ({ ...x, [k]: v }));
  const fehler = regelFehler(e);
  const gewerkOpt = kategorien.filter(k => k.aktiv || k.id === e.gewerk_id).map(k => ({ id: k.id, label: k.name, sub: k.ist_handwerk ? 'Handwerk' : 'Dienstleistung' }));
  const ticketOpt = ticketKategorien.filter(k => k.aktiv !== false || k.id === e.kategorie_id).map(k => ({ id: k.id, label: k.name }));
  const teamOpt = teams.map(t => ({ id: t.id, label: t.name }));
  const gewaehlt = (opt: readonly { id: string; label: string }[], id: string) => opt.find(o => o.id === id) ?? null;
  const vorschlag = besterVorschlag({ gewerk_id: e.gewerk_id || null, ist_notfall: e.ist_notfall }, kandidaten);
  return (
    <InnenKarte>
      <KartenTitel>{neu ? 'Neue Regel' : 'Regel bearbeiten'}</KartenTitel>
      <FeldRaster minBreite={200}>
        <TextFeld label="Bezeichnung" icon={Funnel} placeholder="z. B. Rohrbruch" wert={e.name} onChange={setze('name')} />
        <TextFeld label="Stichwörter, kommagetrennt" icon={Tag} placeholder="z. B. wasser, leck, rohr" wert={e.stichwoerter} onChange={setze('stichwoerter')} />
      </FeldRaster>
      <SegmentedControl<Prioritaet> ariaLabel="Priorität" hoehe={34} className="self-start" wert={e.prioritaet} onChange={setze('prioritaet')}
        segmente={PRIORITAETEN.map(p => ({ key: p.key, label: p.label }))} />
      <div className="flex flex-col" style={{ gap: 6 }}>
        <SchalterZeile an={e.ist_notfall} onChange={setze('ist_notfall')} label="Notfall (sofort an Notdienst)" />
        <SchalterZeile an={e.nur_mit_anhang} onChange={setze('nur_mit_anhang')} label="Nur mit Anhang (Foto)" />
        <SchalterZeile an={e.ist_rechtlich_sensibel} onChange={setze('ist_rechtlich_sensibel')} label="Rechtlich sensibel (an Team Recht)" />
        {!neu && <SchalterZeile an={e.aktiv} onChange={setze('aktiv')} label="Regel aktiv" />}
      </div>
      <FeldRaster minBreite={220}>
        <SearchSelect label="Gewerk (Dienstleister-Vorschlag)" optionen={gewerkOpt} ausgewaehlt={gewaehlt(gewerkOpt, e.gewerk_id)} minZeichen={0}
          onWahl={o => setze('gewerk_id')(o.id)} onEntfernen={() => setze('gewerk_id')('')} platzhalter="Gewerk suchen …" />
        <SearchSelect label="Ticket-Kategorie" optionen={ticketOpt} ausgewaehlt={gewaehlt(ticketOpt, e.kategorie_id)} minZeichen={0}
          onWahl={o => setze('kategorie_id')(o.id)} onEntfernen={() => setze('kategorie_id')('')} platzhalter="Ticket-Kategorie suchen …" />
        <SearchSelect label="Bearbeiter-Team (optional)" optionen={teamOpt} ausgewaehlt={gewaehlt(teamOpt, e.bearbeiter_team_id)} minZeichen={0}
          onWahl={o => setze('bearbeiter_team_id')(o.id)} onEntfernen={() => setze('bearbeiter_team_id')('')} platzhalter="Team suchen …" />
      </FeldRaster>
      <span style={{ fontSize: 12, color: 'var(--ink3)' }}>
        {e.gewerk_id ? (vorschlag ? `Vorschlag: ${vorschlag.name}` : 'Kein passender aktiver Dienstleister – Vorschlag bleibt leer.') : 'Ohne Gewerk wird kein Dienstleister vorgeschlagen.'}
      </span>
      {fehler && (e.name || e.stichwoerter) && <span style={{ fontSize: 12, color: 'var(--bad)' }}>{fehler}</span>}
      <div className="flex flex-wrap" style={{ gap: 8 }}>
        <ImmoButton art="akzent" icon={laeuft ? CircleNotch : Check} disabled={laeuft || !!fehler} onClick={() => onSpeichern(e)}>Regel speichern</ImmoButton>
        <ImmoButton icon={X} disabled={laeuft} onClick={onAbbrechen}>Abbrechen</ImmoButton>
        {onLoeschen && <ImmoButton art="bad" icon={Trash} disabled={laeuft} onClick={onLoeschen}>Regel löschen</ImmoButton>}
      </div>
    </InnenKarte>
  );
}
